import React from 'react';
import { Box, Typography, FormGroup, FormControlLabel, Checkbox, Paper, Button } from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import { OrganizationField } from '../types/reportBuilder.types';

interface FieldSelectionStepProps {
  selectedFields: OrganizationField[];
  onFieldsChange: (fields: OrganizationField[]) => void;
  onImportConfig?: (file: File) => void;
}

const FIELD_GROUPS: { label: string; fields: { value: OrganizationField; label: string }[] }[] = [
  {
    label: 'Organización',
    fields: [
      { value: 'inspectedOrgId', label: 'ID de Organización' },
      { value: 'inspectedOrgName', label: 'Nombre de Finca' },
      { value: 'inspectedOrgLogo', label: 'Logo' },
      { value: 'inspectedOrgRegionName', label: 'Región' },
      { value: 'inspectedOrgCityName', label: 'Ciudad' },
      { value: 'inspectedOrgVeredaName', label: 'Vereda' },
      { value: 'inspectedOrgGroupsName', label: 'Grupos' },
    ],
  },
  {
    label: 'Productor',
    fields: [
      { value: 'inspectedUserId', label: 'ID de Usuario' },
      { value: 'inspectedUserIdNumber', label: 'Cédula' },
      { value: 'inspectedUserName', label: 'Nombre' },
      { value: 'inspectedUserPhones', label: 'Teléfonos' },
    ],
  },
];

const FieldSelectionStep: React.FC<FieldSelectionStepProps> = ({
  selectedFields,
  onFieldsChange,
  onImportConfig,
}) => {
  const handleImportClick = () => {
    if (!onImportConfig) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        onImportConfig(file);
      }
    };
    input.click();
  };
  const handleToggle = (field: OrganizationField) => {
    if (selectedFields.includes(field)) {
      onFieldsChange(selectedFields.filter(f => f !== field));
    } else {
      onFieldsChange([...selectedFields, field]);
    }
  };

  const handleToggleGroup = (groupFields: OrganizationField[]) => {
    const allSelected = groupFields.every(f => selectedFields.includes(f));
    if (allSelected) {
      onFieldsChange(selectedFields.filter(f => !groupFields.includes(f)));
    } else {
      const newFields = [...selectedFields];
      groupFields.forEach(f => {
        if (!newFields.includes(f)) {
          newFields.push(f);
        }
      });
      onFieldsChange(newFields);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Selecciona los campos de organización y productor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Selecciona los campos que deseas incluir en el reporte. Estos campos aparecen en la tabla de retos.
          </Typography>
        </Box>
        {onImportConfig && (
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={handleImportClick}
            sx={{ ml: 2 }}
          >
            Importar Configuración
          </Button>
        )}
      </Box>

      {FIELD_GROUPS.map((group, groupIndex) => (
        <Paper key={groupIndex} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Checkbox
              checked={group.fields.every(f => selectedFields.includes(f.value))}
              indeterminate={
                group.fields.some(f => selectedFields.includes(f.value)) &&
                !group.fields.every(f => selectedFields.includes(f.value))
              }
              onChange={() => handleToggleGroup(group.fields.map(f => f.value))}
              size="small"
            />
            <Typography variant="subtitle1" fontWeight={600}>
              {group.label}
            </Typography>
          </Box>
          <FormGroup sx={{ pl: 4 }}>
            {group.fields.map((field) => (
              <FormControlLabel
                key={field.value}
                control={
                  <Checkbox
                    checked={selectedFields.includes(field.value)}
                    onChange={() => handleToggle(field.value)}
                    size="small"
                  />
                }
                label={field.label}
              />
            ))}
          </FormGroup>
        </Paper>
      ))}

      {selectedFields.length === 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
          ⚠️ No se han seleccionado campos. Selecciona al menos uno para continuar.
        </Typography>
      )}
    </Box>
  );
};

export default FieldSelectionStep;
